import {
  Box3,
  Group,
  Material,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Vector3
} from "three";
import { GLBModel } from "./GLBModel";
import { getCleanerHosePose } from "./CleanerHosePose";

const CLEANER_MODEL_SCALE = 0.326;
const CLEANER_CAPSULE_MATERIAL_NAME = "m_cleaner.003";
const CLEANER_CAPSULE_ALPHA_BOOST = 0.34;
const CLEANER_HOSE_EXTEND_SPEED = 1.45;
const CLEANER_HOSE_RETRACT_SPEED = 3.35;

const CLEANER_HOSE_LENGTH_BONE_NAMES = Array.from(
  { length: 40 },
  (_, index) => `Bone${String(index + 2).padStart(3, "0")}`
);

const getBoneNameVariants = (name: string) => {
  const variants = [name];
  const compactMatch = /^Bone(\d+)$/.exec(name);
  const dotMatch = /^Bone\.(\d+)$/.exec(name);

  if (compactMatch) {
    variants.push(`Bone.${compactMatch[1].padStart(3, "0")}`);
  }

  if (dotMatch) {
    variants.push(`Bone${dotMatch[1].padStart(3, "0")}`);
  }

  return variants;
};

const findObjectByFlexibleName = (root: Object3D, name: string) => {
  for (const variant of getBoneNameVariants(name)) {
    const object = root.getObjectByName(variant);

    if (object) return object;
  }

  return null;
};

const boostCleanerCapsuleOpacity = (root: Object3D) => {
  const cloneBoostedMaterial = (material: Material) => {
    if (
      material.name !== CLEANER_CAPSULE_MATERIAL_NAME ||
      !(material instanceof MeshStandardMaterial)
    ) {
      return material;
    }

    const clonedMaterial = material.clone();
    clonedMaterial.transparent = true;
    clonedMaterial.opacity = 1;
    clonedMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <map_fragment>",
        `#include <map_fragment>
diffuseColor.a = mix(diffuseColor.a, 1.0, ${CLEANER_CAPSULE_ALPHA_BOOST.toFixed(2)});`
      );
    };
    clonedMaterial.customProgramCacheKey = () =>
      `cleaner-capsule-alpha-${CLEANER_CAPSULE_ALPHA_BOOST}`;
    clonedMaterial.needsUpdate = true;

    return clonedMaterial;
  };

  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;

    if (Array.isArray(object.material)) {
      object.material = object.material.map(cloneBoostedMaterial);
    } else {
      object.material = cloneBoostedMaterial(object.material);
    }
  });
};

export class CleanerModel extends GLBModel {
  constructor() {
    super("/models/weapon/cleaner_04.glb");
  }

  get key(): string {
    return "cleaner";
  }

  /**
   * Как в donmccurdy/three-gltf-viewer `setContent`: AABB → сдвиг корня, чтобы пивот был у центра.
   * Внешний `Group` с identity-трансформом — сюда в сцене вешают смещение от камеры, не затирая центрирование.
   */
  getModel(): Object3D | null {
    const scene = super.getModel();

    if (!scene) {
      return null;
    }

    scene.updateMatrixWorld(true);
    const center = new Box3().setFromObject(scene).getCenter(new Vector3());
    scene.scale.multiplyScalar(CLEANER_MODEL_SCALE);
    scene.position.addScaledVector(center, -CLEANER_MODEL_SCALE);
    boostCleanerCapsuleOpacity(scene);

    const hudRoot = new Group();
    hudRoot.add(scene);

    hudRoot.traverse((obj) => {
      obj.frustumCulled = false;
      obj.layers.set(1);
    });

    return hudRoot;
  }

  /**
   * Первый клип GLB на клоне сцены. Требуется {@link GLBModel.getModel} через
   * `SkeletonUtils.clone`, иначе скелет смотрит на кости оригинала и микшер не деформирует меш.
   */
  createHudAnimationDriver(hudRoot: Object3D): {
    update: (dt: number, extend: boolean) => void;
    getProgress: () => number;
    dispose: () => void;
  } | null {
    const inner = hudRoot.children[0];
    if (!inner) {
      return null;
    }

    const hoseLengthBones = CLEANER_HOSE_LENGTH_BONE_NAMES
      .map((name) => findObjectByFlexibleName(inner, name))
      .filter((bone): bone is Object3D => bone !== null);
    const hoseLengthBoneRestPositions = hoseLengthBones.map((bone) =>
      bone.position.clone()
    );
    let progress = 0;

    const applyHosePose = () => {
      const pose = getCleanerHosePose(progress);

      hoseLengthBones.forEach((bone, index) => {
        bone.position
          .copy(hoseLengthBoneRestPositions[index])
          .multiplyScalar(pose.lengthScale);
      });
    };

    applyHosePose();

    return {
      update: (dt: number, extend: boolean) => {
        const target = extend ? 1 : 0;
        const speed = extend
          ? CLEANER_HOSE_EXTEND_SPEED
          : CLEANER_HOSE_RETRACT_SPEED;
        const nextProgress = MathUtils.clamp(
          progress + Math.sign(target - progress) * dt * speed,
          0,
          1
        );

        progress = nextProgress;
        applyHosePose();
      },
      getProgress: () => {
        return progress;
      },
      dispose: () => {
      }
    };
  }
}
